// utils/mexicoSearchSystem.js - Mexico-specific search system with flag support
import { mexicoFeedsConfig, getMexicanStateInfo, getMexicanStateFeedsByBranch } from './mexicoFeedsConfig.js';
import { mexicoDepartmentConfig, getMexicoDepartmentConfig } from './mexicoDepartmentConfig.js';

// Mexican state information lookup (from your existing config)
const mexicoStateInfo = {
  aguascalientes: { fullName: 'Aguascalientes', code: 'ags', flagImage: '/aguascalientesFlag.png', tableName: 'aguascalientes' },
  bajacalifornia: { fullName: 'Baja California', code: 'bc', flagImage: '/bajaCaliforniaFlag.png', tableName: 'baja_california' },
  bajacaliforniasur: { fullName: 'Baja California Sur', code: 'bcs', flagImage: '/bajaCaliforniaSurFlag.png', tableName: 'baja_california_sur' },
  campeche: { fullName: 'Campeche', code: 'camp', flagImage: '/campecheFlag.png', tableName: 'campeche' },
  chiapas: { fullName: 'Chiapas', code: 'chis', flagImage: '/chiapasFlag.png', tableName: 'chiapas' },
  chihuahua: { fullName: 'Chihuahua', code: 'chih', flagImage: '/chihuahuaFlag.png', tableName: 'chihuahua' },
  coahuila: { fullName: 'Coahuila', code: 'coah', flagImage: '/coahuilaFlag.png', tableName: 'coahuila' },
  colima: { fullName: 'Colima', code: 'col', flagImage: '/colimaFlag.png', tableName: 'colima' },
  durango: { fullName: 'Durango', code: 'dur', flagImage: '/durangoFlag.png', tableName: 'durango' },
  guanajuato: { fullName: 'Guanajuato', code: 'gto', flagImage: '/guanajuatoFlag.png', tableName: 'guanajuato' },
  guerrero: { fullName: 'Guerrero', code: 'gro', flagImage: '/guerreroFlag.png', tableName: 'guerrero' },
  hidalgo: { fullName: 'Hidalgo', code: 'hgo', flagImage: '/hidalgoFlag.png', tableName: 'hidalgo' },
  jalisco: { fullName: 'Jalisco', code: 'jal', flagImage: '/jaliscoFlag.png', tableName: 'jalisco' },
  mexicocity: { fullName: 'Ciudad de México', code: 'cdmx', flagImage: '/mexicoCityFlag.png', tableName: 'mexico_city' },
  mexicostate: { fullName: 'Estado de México', code: 'edomex', flagImage: '/mexicoStateFlag.png', tableName: 'mexico_state' },
  michoacan: { fullName: 'Michoacán', code: 'mich', flagImage: '/michoacanFlag.png', tableName: 'michoacan' },
  morelos: { fullName: 'Morelos', code: 'mor', flagImage: '/morelosFlag.png', tableName: 'morelos' },
  nayarit: { fullName: 'Nayarit', code: 'nay', flagImage: '/nayaritFlag.png', tableName: 'nayarit' },
  nuevoleon: { fullName: 'Nuevo León', code: 'nl', flagImage: '/nuevoLeonFlag.png', tableName: 'nuevo_leon' },
  oaxaca: { fullName: 'Oaxaca', code: 'oax', flagImage: '/oaxacaFlag.png', tableName: 'oaxaca' },
  puebla: { fullName: 'Puebla', code: 'pue', flagImage: '/pueblaFlag.png', tableName: 'puebla' },
  queretaro: { fullName: 'Querétaro', code: 'qro', flagImage: '/queretaroFlag.png', tableName: 'queretaro' },
  quintanaroo: { fullName: 'Quintana Roo', code: 'qroo', flagImage: '/quintanaRooFlag.png', tableName: 'quintana_roo' },
  sanluispotosi: { fullName: 'San Luis Potosí', code: 'slp', flagImage: '/sanLuisPotosiFlag.png', tableName: 'san_luis_potosi' },
  sinaloa: { fullName: 'Sinaloa', code: 'sin', flagImage: '/sinaloaFlag.png', tableName: 'sinaloa' },
  sonora: { fullName: 'Sonora', code: 'son', flagImage: '/sonoraFlag.png', tableName: 'sonora' },
  tabasco: { fullName: 'Tabasco', code: 'tab', flagImage: '/tabascoFlag.png', tableName: 'tabasco' },
  tamaulipas: { fullName: 'Tamaulipas', code: 'tamps', flagImage: '/tamaulipasFlag.png', tableName: 'tamaulipas' },
  tlaxcala: { fullName: 'Tlaxcala', code: 'tlax', flagImage: '/tlaxcalaFlag.png', tableName: 'tlaxcala' },
  veracruz: { fullName: 'Veracruz', code: 'ver', flagImage: '/veracruzFlag.png', tableName: 'veracruz' },
  yucatan: { fullName: 'Yucatán', code: 'yuc', flagImage: '/yucatanFlag.png', tableName: 'yucatan' },
  zacatecas: { fullName: 'Zacatecas', code: 'zac', flagImage: '/zacatecasFlag.png', tableName: 'zacatecas' }
};

// Search relevant pages for Mexico - similar to US/Canada but Mexico-specific
export const searchMexicoRelevantPages = (query, limit = 12) => {
  if (!query || !query.trim()) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  const results = [];
  
  // console.log('=== MEXICO SEARCH DEBUG ===');
  // console.log('Query:', normalizedQuery);

  // 1. Search Mexican states - exact name matching
  Object.entries(mexicoStateInfo).forEach(([stateKey, stateData]) => {
    const fullName = stateData.fullName.toLowerCase();
    
    if (fullName.includes(normalizedQuery) || normalizedQuery.includes(fullName)) {
      // Main state page - NOW WITH FLAG IMAGE
      results.push({
        title: `${stateData.fullName} Page`,
        description: `${stateData.fullName} state government information and updates`,
        route: `/mexico/${stateKey}`,
        icon: 'Map',
        flagImage: stateData.flagImage, // ADD THIS LINE
        type: 'state',
        priority: 1
      });
      
      // State feeds
      results.push({
        title: `${stateData.fullName} Updates`,
        description: `View all updates from ${stateData.fullName}`,
        route: `/feed/mexico/${stateData.tableName}`,
        icon: 'FileText',
        type: 'feed',
        priority: 2
      });
      
      // console.log('Found Mexican state match:', stateData.fullName);
    }
  });

  // 2. Search secretarías (departments/ministries)
  Object.entries(mexicoDepartmentConfig).forEach(([deptKey, deptData]) => {
    if (!deptData.departmentInfo) return;
    
    const deptName = deptData.departmentInfo.name.toLowerCase();
    
    if (deptName.includes(normalizedQuery) || 
        deptName.replace('secretaría de ', '').includes(normalizedQuery) ||
        deptName.replace('secretaría del ', '').includes(normalizedQuery)) {
      
      // Main department page
      results.push({
        title: deptData.departmentInfo.name,
        description: deptData.departmentInfo.missionStatement || 'Mexican government ministry information and services',
        route: deptData.departmentInfo.route,
        icon: 'Building',
        type: 'department',
        priority: 1
      });

      // Add department main feeds
      if (deptData.mainFeeds && deptData.mainFeeds.length > 0) {
        deptData.mainFeeds.forEach(feed => {
          if (feed.apiParams) {
            const schemaMatch = feed.apiParams.match(/schema=([^&]+)/);
            const tableMatch = feed.apiParams.match(/table=([^&]+)/);
            
            if (schemaMatch && tableMatch) {
              const schema = decodeURIComponent(schemaMatch[1]);
              const table = decodeURIComponent(tableMatch[1]);
              
              let feedRoute = `/feed/${schema}/${table}`;
              
              const filterMatch = feed.apiParams.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
              if (filterMatch) {
                const filterColumn = decodeURIComponent(filterMatch[1]);
                const filterString = decodeURIComponent(filterMatch[2]);
                feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
              }
              
              results.push({
                title: `${deptData.departmentInfo.name} - ${feed.name}`,
                description: `View all ${feed.name.toLowerCase()} from ${deptData.departmentInfo.name}`,
                route: feedRoute,
                icon: 'List',
                type: 'feed',
                priority: 2
              });
            }
          }
        });
      }

      // console.log('Found Mexican department match:', deptData.departmentInfo.name);
    }
  });

  // 3. Search sections (legislative, executive, judicial, etc.)
  const sectionMappings = {
    'legislative': { 
      title: 'Legislative Branch', 
      description: 'Congress, Senate, and legislative proceedings (Poder Legislativo)'
    },
    'executive': { 
      title: 'Executive Branch', 
      description: 'Presidency and government ministries (Poder Ejecutivo)'
    },
    'judicial': { 
      title: 'Judicial Branch', 
      description: 'Supreme Court decisions and judicial proceedings (Poder Judicial)'
    },
    'economy': { 
      title: 'Economy Section', 
      description: 'Economic policy, finance, and trade (Economía)'
    },
    'environment': { 
      title: 'Environment Section', 
      description: 'Environmental policy and natural resources (Medio Ambiente)'
    },
    'health': { 
      title: 'Health Section', 
      description: 'Public health and social welfare (Salud)'
    },
    'defense': { 
      title: 'Defense Section', 
      description: 'National defense and security (Defensa Nacional)'
    },
    'foreign-affairs': { 
      title: 'Foreign Affairs', 
      description: 'International relations and diplomacy (Relaciones Exteriores)'
    },
    'infrastructure': { 
      title: 'Infrastructure Section', 
      description: 'Transportation and communications infrastructure (Infraestructura)'
    }
  };

  Object.entries(sectionMappings).forEach(([sectionKey, sectionInfo]) => {
    const sectionTitle = sectionInfo.title.toLowerCase();
    
    if (sectionKey.includes(normalizedQuery) || 
        sectionTitle.includes(normalizedQuery) ||
        normalizedQuery === sectionKey) {
      
      // Main section page
      results.push({
        title: sectionInfo.title,
        description: sectionInfo.description,
        route: `/mexico/${sectionKey}`,
        icon: 'Globe',
        type: 'section',
        priority: 3
      });

      // Add feeds from this section
      if (mexicoFeedsConfig[sectionKey] && Array.isArray(mexicoFeedsConfig[sectionKey])) {
        mexicoFeedsConfig[sectionKey].forEach(feed => {
          if (feed.apiEndpoint && feed.title && feed.showOnHomePage !== false) {
            const schemaMatch = feed.apiEndpoint.match(/schema=([^&]+)/);
            const tableMatch = feed.apiEndpoint.match(/table=([^&]+)/);
            
            if (schemaMatch && tableMatch) {
              const schema = decodeURIComponent(schemaMatch[1]);
              const table = decodeURIComponent(tableMatch[1]);
              
              let feedRoute = `/feed/${schema}/${table}`;
              
              const filterMatch = feed.apiEndpoint.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
              if (filterMatch) {
                const filterColumn = decodeURIComponent(filterMatch[1]);
                const filterString = decodeURIComponent(filterMatch[2]);
                feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
              }
              
              results.push({
                title: feed.title,
                description: `View all items in the ${feed.title.toLowerCase()} feed`,
                route: feedRoute,
                icon: 'List',
                type: 'feed',
                priority: 4
              });
            }
          }
        });
      }

      // console.log('Found Mexican section match:', sectionInfo.title);
    }
  });

  // 4. Search individual feeds by name
  Object.entries(mexicoFeedsConfig).forEach(([sectionKey, sectionFeeds]) => {
    if (!Array.isArray(sectionFeeds)) return;
    
    sectionFeeds.forEach(feed => {
      if (!feed.title || !feed.apiEndpoint) return;
      
      const feedTitle = feed.title.toLowerCase();
      const feedName = (feed.feedName || '').toLowerCase();
      
      if (feedTitle.includes(normalizedQuery) || 
          feedName.includes(normalizedQuery) ||
          normalizedQuery.includes(feedTitle)) {
        
        const schemaMatch = feed.apiEndpoint.match(/schema=([^&]+)/);
        const tableMatch = feed.apiEndpoint.match(/table=([^&]+)/);
        
        if (schemaMatch && tableMatch) {
          const schema = decodeURIComponent(schemaMatch[1]);
          const table = decodeURIComponent(tableMatch[1]);
          
          let feedRoute = `/feed/${schema}/${table}`;
          
          const filterMatch = feed.apiEndpoint.match(/filter_column=([^&]+)&filter_string=([^&]+)/);
          if (filterMatch) {
            const filterColumn = decodeURIComponent(filterMatch[1]);
            const filterString = decodeURIComponent(filterMatch[2]);
            feedRoute += `?filter_column=${filterColumn}&filter_string=${encodeURIComponent(filterString)}`;
          }
          
          results.push({
            title: feed.title,
            description: `View all items in the ${feed.title.toLowerCase()} feed`,
            route: feedRoute,
            icon: 'List',
            type: 'feed',
            priority: 3
          });
          
          // console.log('Found Mexican feed match:', feed.title);
        }
      }
    });
  });

  // Remove duplicates and sort by priority
  const uniqueResults = results.filter((item, index, self) => 
    index === self.findIndex(t => t.route === item.route)
  );

  const finalResults = uniqueResults
    .sort((a, b) => a.priority - b.priority)
    .slice(0, limit);

  // console.log('Final Mexico results:', finalResults.length);
  // console.log('=== END MEXICO SEARCH ===');
  
  return finalResults;
};

// Enhanced content type detection for Mexico
export const enrichMexicoSearchResult = (item) => {
  try {
    const title = (item.title || '').toLowerCase();
    const description = (item.description || item.snippet || '').toLowerCase();
    const source = (item.source || '').toLowerCase();
    const collectionName = (item.collection_name || '').toLowerCase();

    // Check for Mexican department patterns
    for (const [deptKey, deptConfig] of Object.entries(mexicoDepartmentConfig)) {
      if (!deptConfig.departmentInfo) continue;
      
      const deptName = deptConfig.departmentInfo.name.toLowerCase();
      const deptKeywords = [
        deptName,
        deptConfig.departmentInfo.name.replace(/secretaría de /i, ''),
        deptConfig.departmentInfo.name.replace(/secretaría del /i, ''),
        deptKey
      ];

      if (deptKeywords.some(keyword => 
        title.includes(keyword) || 
        source.includes(keyword) || 
        collectionName.includes(keyword)
      )) {
        return {
          type: 'department',
          departmentKey: deptKey,
          department: deptConfig.departmentInfo,
          route: deptConfig.departmentInfo.route
        };
      }
    }

    // Check for Mexican state patterns
    for (const [stateKey, stateData] of Object.entries(mexicoStateInfo)) {
      const stateName = stateData.fullName.toLowerCase();
      
      if (title.includes(stateName) || 
          source.includes(stateName) || 
          collectionName.includes(stateName)) {
        return {
          type: 'state',
          stateKey: stateKey,
          state: stateData,
          route: `/mexico/${stateKey}`
        };
      }
    }

    // Check for section patterns
    const sectionKeywords = {
      'legislative': ['senado', 'congress', 'diputados', 'chamber', 'legislative'],
      'executive': ['presidente', 'president', 'secretaría', 'ministry', 'gobierno'],
      'judicial': ['suprema corte', 'supreme court', 'tribunal', 'judicial'],
      'defense': ['defensa', 'defense', 'sedena', 'semar', 'marina'],
      'economy': ['economía', 'economy', 'hacienda', 'finance', 'turismo'],
      'environment': ['medio ambiente', 'environment', 'semarnat', 'agricultura'],
      'health': ['salud', 'health', 'bienestar', 'welfare']
    };

    for (const [sectionKey, keywords] of Object.entries(sectionKeywords)) {
      if (keywords.some(keyword => title.includes(keyword) || description.includes(keyword))) {
        return {
          type: 'section',
          sectionKey: sectionKey,
          route: `/mexico/${sectionKey}`
        };
      }
    }

    return { type: 'document' };
  } catch (error) {
    console.warn('Error in enrichMexicoSearchResult:', error);
    return { type: 'document' };
  }
};

// Export functions for use in components
export const getMexicoNavigationSuggestions = (query, limit = 6) => {
  if (!query || !query.trim()) return [];
  
  const suggestions = searchMexicoRelevantPages(query, limit);
  
  return suggestions.map(suggestion => ({
    title: suggestion.title,
    description: suggestion.description,
    route: suggestion.route,
    type: suggestion.type,
    icon: getIconForType(suggestion.type),
    priority: suggestion.priority || 5
  }));
};

const getIconForType = (type) => {
  const iconMap = {
    'department': 'Building',
    'agency': 'Building',
    'state': 'Map', // Keep as fallback
    'section': 'Globe',
    'feed': 'List',
    'document': 'FileText'
  };
  
  return iconMap[type] || 'InfoCircle';
};

export default {
  searchMexicoRelevantPages,
  enrichMexicoSearchResult,
  getMexicoNavigationSuggestions
};